import { useEffect, useState } from "react";
import axios from "axios";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
} from "@mui/material";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import { PopperFullWidth, getInputLabelProps } from "../../../common/appStyles";
import { getOperationalStatusNameFromId, MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../../common/utils";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function FinishEventForm({ finishReview, setFinishReview }) {
  const { PERSONAL_API, config } = useGlobalContext();
  const maximumCharactersForDescription = MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [finishObservationCount, setFinishObservationCount] = useState(0);
  const [personalData, setPersonalData] = useState(null);

  const getAvailableOperationalStatuses = () => {
    if (finishReview.OperationalStatusId === 0) {
      return [
        {
          Id: 0,
          Name: getOperationalStatusNameFromId(0),
        },
      ];
    }

    return [
      {
        Id: 3,
        Name: getOperationalStatusNameFromId(3),
      },
      {
        Id: 4,
        Name: getOperationalStatusNameFromId(4),
      },
      {
        Id: 5,
        Name: getOperationalStatusNameFromId(5),
      },
    ];
  };

  const getPersonal = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PERSONAL_API}/`, requestConfig);

    setPersonalData(data?.data);
  };

  const handleOperationalStatusChange = (e) => {
    setFinishReview({
      ...finishReview,
      OperationalStatusId: !e ? null : e.Id,
      OperationalStatusSelected: e,
      CanEdit: e?.Id === 3 ? 1 : finishReview.CanEdit,
    });
  };

  const handleObservationChange = (e) => {
    setFinishReview({
      ...finishReview,
      Observation: e.target.value,
    });
  };

  const handlePersonalChange = (newPersonal) => {
    setFinishReview({
      ...finishReview,
      PersonalAssigned: newPersonal,
      PersonalId: !newPersonal ? 0 : newPersonal.id,
    });
  };

  const handleCanEditChange = (checkbox) => {
    setFinishReview({
      ...finishReview,
      CanEdit: checkbox.target.checked ? 1 : 0,
    });
  };

  useEffect(() => {
    getPersonal();
  }, []);

  useEffect(() => {
    if (finishReview?.Observation) {
      setFinishObservationCount(finishReview?.Observation.length);
    }
  }, [finishReview?.Observation]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="mt-2">
        <Combobox
          isRequired
          disabled={finishReview.OperationalStatusId === 0}
          fieldValue={finishReview.OperationalStatusSelected}
          fieldLabel={localize.operational_status}
          handleValueChange={handleOperationalStatusChange}
          fieldOptions={getAvailableOperationalStatuses()}
        />
      </div>

      {finishReview.OperationalStatusId !== 0 && (
        <div className="pt-2">
          <div>
            <FormControlLabel
              size="small"
              name="CanEdit"
              disabled={finishReview.OperationalStatusId === 3}
              control={
                <Checkbox
                  size="small"
                  name="CanEdit"
                  checked={finishReview.CanEdit === 1}
                  onChange={handleCanEditChange}
                />
              }
              label={
                <span style={{ fontSize: "0.8em" }}>
                  {finishReview.CanEdit === 1
                    ? localize.can_edit
                    : localize.finish_review_warning}
                </span>
              }
            />
          </div>
        </div>
      )}

      <div className="pt-5">
        <FormControl required fullWidth>
          <TextField
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              style: {
                fontSize: "0.8em",
              },
              maxLength: maximumCharactersForDescription,
            }}
            size="small"
            fullWidth
            multiline
            rows={5}
            id="outlined-basic"
            name="FinishObservationText"
            label={localize.conclusions}
            variant="outlined"
            onChange={handleObservationChange}
            value={finishReview?.Observation ?? ""}
            helperText={`${finishObservationCount}/${maximumCharactersForDescription}`}
          />
        </FormControl>
      </div>

      {finishReview.OperationalStatusId !== 0 && (
        <div className="pt-2">
          <Combobox
            id="PersonalAssigned"
            fieldLabel={localize.assigned_to}
            fieldOptions={personalData?.Data ?? []}
            fieldValue={finishReview?.PersonalAssigned}
            customOptionLabel={(option) =>
              `${option.Identification} - ${option.SurName} ${option.LastName}`
            }
            handleValueChange={handlePersonalChange}
            popperFullWidth={PopperFullWidth}
          />
        </div>
      )}
    </div>
  );
}
